import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCreateManySequestrationRulesetsMutation,
  useCreateSequestrationRulesetMutation,
  useGetSequestrationRulesetQuery,
} from "store/queries/sequestrationRulesets";

import {
  ISequestrationRulesetFormData,
  INITIAL_SEQUESTRATION_RULESET_FORM_DATA,
  SequestrationRulesetForm,
} from "./components/SequestrationRulesetForm";

export function CopySequestrationRulesetPage() {
  useDocumentTitle("Copy Sequester Rule");
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data: response, isLoading } = useGetSequestrationRulesetQuery(id, {
    skip: !id,
  });
  const [createSingle] = useCreateSequestrationRulesetMutation();
  const [createMany] = useCreateManySequestrationRulesetsMutation();
  const [formData, setFormData] = useState<ISequestrationRulesetFormData>(
    INITIAL_SEQUESTRATION_RULESET_FORM_DATA
  );

  // Map the fetched row into the form's multi-select-shaped state. A copy is a new scoped
  // rule by default (only one default row may exist), so scope is dropped unless the caller
  // explicitly asks to copy as the default via ?asDefault=true.
  useEffect(() => {
    const row = response?.data;
    if (!row) return;
    setFormData({
      providerIds: asDefault || !row.providerId ? [] : [row.providerId],
      payerIds: asDefault || row.payerId == null ? [] : [row.payerId],
      percent: Number(row.percent),
      isDefault: asDefault,
      contractLanguage: row.contractLanguage ?? "",
    });
  }, [response, asDefault]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Default-rule creation goes through the single endpoint; everything else fans out.
      if (formData.isDefault) {
        await createSingle({
          isDefault: true,
          percent: formData.percent,
          contractLanguage: formData.contractLanguage || null,
        }).unwrap();
      } else {
        await createMany({
          providerIds: formData.providerIds,
          payerIds: formData.payerIds,
          percent: formData.percent,
          contractLanguage: formData.contractLanguage || null,
        }).unwrap();
      }
      toastElement?.show({
        severity: "success",
        summary: "Sequester rule copied",
      });
      navigate("/payer-rules");
    } catch (err) {
      const detail =
        (err as { data?: { message?: string } })?.data?.message ??
        "Failed to copy sequester rule";
      toastElement?.show({
        severity: "error",
        summary: "Could not copy sequester rule",
        detail,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <SequestrationRulesetForm
      title="Copy Sequester Rule"
      submitLabel="Create Copy"
      mode="create"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
